import { useState } from "react";
import { Link } from "react-router-dom";
import { Sprout, BookOpen, Landmark, Briefcase, Leaf } from "lucide-react";
import { isPlaceholder } from "@/lib/api";

export const PILLARS = {
  green: { Icon: Sprout, border: "border-t-irid-green", icon: "text-irid-green", text: "text-irid-green-deep", tint: "bg-irid-green/10", bar: "bg-irid-green" },
  teal: { Icon: BookOpen, border: "border-t-irid-teal", icon: "text-irid-teal", text: "text-irid-teal-deep", tint: "bg-irid-teal/10", bar: "bg-irid-teal" },
  blue: { Icon: Landmark, border: "border-t-irid-blue", icon: "text-irid-blue", text: "text-irid-blue", tint: "bg-irid-blue/10", bar: "bg-irid-blue" },
  gold: { Icon: Briefcase, border: "border-t-irid-gold", icon: "text-irid-orange-deep", text: "text-irid-orange-deep", tint: "bg-irid-gold/15", bar: "bg-irid-gold" },
};

export function Txt({ children, as: Tag = "span", className = "" }) {
  if (isPlaceholder(children)) {
    return <Tag className={className}><span className="irid-ph" title="Content awaiting IRID approval">{children}</span></Tag>;
  }
  return <Tag className={className}>{children}</Tag>;
}

export function Logo({ size = 64, testid = "site-logo" }) {
  const [failed, setFailed] = useState(false);
  if (!failed) {
    return (
      <img src="/brand/irid-logo.png" alt="IRID – The Institute for Research, Innovation and Development, home" width={Math.round(size * 1.06)} height={size}
        style={{ height: size, width: "auto" }} className="block max-w-none" decoding="async" onError={() => setFailed(true)} data-testid={testid} />
    );
  }
  return (
    <span role="img" aria-label="IRID – The Institute for Research, Innovation and Development, home (logo placeholder awaiting master file)"
      title="[PLACEHOLDER – AWAITING IRID APPROVAL: master logo file]" data-testid={testid}
      className="inline-flex shrink-0 items-center justify-center rounded-full border-2 border-dashed border-irid-navy bg-white font-heading font-extrabold text-irid-navy"
      style={{ width: size, height: size, fontSize: size * 0.26 }}>IRID</span>
  );
}

export function Wordmark() {
  return (
    <span className="hidden flex-col leading-tight xl:flex">
      <span className="max-w-[220px] font-heading text-[13px] font-bold uppercase tracking-[0.06em] text-irid-navy">The Institute for Research, Innovation and Development</span>
      <span className="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-irid-slate">Research. Innovation. Institutional Development.</span>
    </span>
  );
}

export function Tagline({ className = "" }) {
  return (
    <p className={`flex items-center gap-2 italic ${className}`} data-testid="brand-tagline">
      <Leaf aria-hidden="true" className="h-4 w-4 text-irid-lime" /> Where Careers and Dreams Grow
    </p>
  );
}

export function SectionHeading({ eyebrow, title, intro, center = false, testid }) {
  return (
    <div className={`mb-10 max-w-3xl ${center ? "mx-auto text-center" : ""}`} data-testid={testid}>
      {eyebrow && <span className="eyebrow mb-4">{eyebrow}</span>}
      <h2 className="mt-2">{title}</h2>
      <div className={`growth-bar mt-4 ${center ? "mx-auto" : ""}`} aria-hidden="true" />
      {intro && <p className="mt-5 text-lg text-irid-slate">{intro}</p>}
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6 text-sm text-irid-mist" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2">
            {i > 0 && <span aria-hidden="true">/</span>}
            {it.to ? <Link to={it.to} className="underline-offset-4 hover:text-irid-gold hover:underline">{it.label}</Link>
              : <span aria-current="page" className="font-bold text-white">{it.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Swoosh({ className = "" }) {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 800 400" className={`pointer-events-none absolute ${className}`} fill="none">
      <path d="M-20 320 C 180 120, 460 60, 820 140" stroke="rgb(var(--irid-teal))" strokeWidth="26" strokeLinecap="round" opacity=".35" />
      <path d="M-20 370 C 220 200, 500 170, 820 230" stroke="rgb(var(--irid-lime))" strokeWidth="14" strokeLinecap="round" opacity=".3" />
      <circle cx="640" cy="110" r="56" fill="rgb(var(--irid-gold))" opacity=".18" />
    </svg>
  );
}

export function PageBanner({ title, intro, crumbs, accent, eyebrow, testid = "page-banner", children }) {
  return (
    <section className="on-dark relative overflow-hidden bg-banner text-white" data-testid={testid}>
      <Swoosh className="-right-24 bottom-0 h-full w-[760px] opacity-70" />
      <div className="container-irid relative py-14 md:py-20">
        {crumbs && <Breadcrumbs items={crumbs} />}
        {eyebrow && <span className="eyebrow mb-3">{eyebrow}</span>}
        <h1 className="mt-2 max-w-4xl text-white">{title}</h1>
        <div className={`mt-5 h-1 w-14 rounded ${accent || "growth-band"}`} aria-hidden="true" />
        {intro && <div className="mt-5 max-w-3xl text-lg text-white/90"><Txt>{intro}</Txt></div>}
        {children}
      </div>
    </section>
  );
}

export function Pagination({ page, pages, onPage, testid = "pagination" }) {
  if (pages <= 1) return null;
  return (
    <nav aria-label="Pagination" className="mt-12 flex flex-wrap items-center gap-2" data-testid={testid}>
      {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
        <button key={p} type="button" onClick={() => onPage(p)} aria-current={p === page ? "page" : undefined}
          data-testid={`${testid}-page-${p}`}
          className={`min-h-[44px] min-w-[44px] rounded-lg border px-3 text-sm ${p === page ? "border-irid-navy bg-irid-navy font-bold text-white" : "border-irid-cloud bg-white text-irid-blue hover:border-irid-blue"}`}>
          {p}
        </button>
      ))}
    </nav>
  );
}
